package com.havn.app.features.dashboard.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnPullToRefreshBox
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.designsystem.havnNeedsNativeTabBarContentInset
import com.havn.app.core.designsystem.havnRefreshStartGate
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.havnAgencyMediaPreviewSource
import com.havn.app.core.media.havnLargePropertyPhotoSource
import com.havn.app.core.platform.havnIsTabletDevice
import com.havn.app.core.platform.havnAppVersionName
import com.havn.app.ui.theme.LocalHavnThemeTokens
import com.havn.app.features.agency.presentation.AgencyUiState
import com.havn.app.features.agency.presentation.AgencyViewModel
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.followups.domain.FollowUpStatus
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.presentation.LeadsViewModel
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseStatus
import com.havn.app.features.openhouses.presentation.OpenHousesViewModel
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.domain.PropertyPhoto
import com.havn.app.features.properties.domain.PropertyStatus
import com.havn.app.features.properties.presentation.PropertiesViewModel
import com.havn.app.features.reports.presentation.SellerReportsViewModel
import com.havn.app.features.agency.domain.Agency
import com.havn.app.features.agency.domain.AgencyInvitation
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.domain.HavnTaskStatus
import com.havn.app.features.tasks.presentation.TasksViewModel
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DashboardScreen(
    authRepository: AuthRepository,
    agencyViewModel: AgencyViewModel,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    tasksViewModel: TasksViewModel,
    sellerReportsViewModel: SellerReportsViewModel,
    onOpenOpening: (String) -> Unit,
    onViewProperty: (String) -> Unit,
    onOpenLeads: () -> Unit,
    onOpenProperties: () -> Unit,
    onOpenReports: () -> Unit,
    onOpenAnalytics: () -> Unit,
    onOpenTasks: () -> Unit,
    onOpenTask: (String) -> Unit,
    onAddProperty: () -> Unit,
    onAddContact: () -> Unit,
    onOpenFiles: () -> Unit,
    onScheduleOpenHouse: (String) -> Unit,
    onOpenSettings: () -> Unit = {},
    onOpenAgency: () -> Unit = {},
    showHeaderActions: Boolean = true,
    canUseFiles: Boolean = true,
    canUseTasks: Boolean = false,
    canUseReports: Boolean = false,
    canUseAnalytics: Boolean = false,
    scrollToTopSignal: Int = 0
) {
    val propertiesState by propertiesViewModel.uiState.collectAsState()
    val openHousesState by openHousesViewModel.uiState.collectAsState()
    val leadsState by leadsViewModel.uiState.collectAsState()
    val followUpsState by followUpsViewModel.uiState.collectAsState()
    val tasksState by tasksViewModel.uiState.collectAsState()
    val authState by authRepository.authState.collectAsState()
    val agencyState by agencyViewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val userId = (authState as? AuthState.LoggedIn)?.user?.id
    var firstName by remember(userId) { mutableStateOf<String?>(null) }
    var showFilesUpgradeDialog by remember { mutableStateOf(false) }
    val openFilesAction: () -> Unit = {
        if (canUseFiles) {
            onOpenFiles()
        } else {
            showFilesUpgradeDialog = true
        }
    }

    LaunchedEffect(userId) {
        firstName = null
        if (userId != null) {
            authRepository.agentProfile(userId)
                .onSuccess { profile -> firstName = profile.firstName.takeIf { it.isNotBlank() } }
        }
    }

    if (showFilesUpgradeDialog) {
        AlertDialog(
            onDismissRequest = { showFilesUpgradeDialog = false },
            title = { Text(strings.filesTitle) },
            text = { Text(strings.attachmentDocumentsNotIncluded) },
            confirmButton = {
                HavnButton(
                    text = strings.upgradePlan,
                    onClick = {
                        showFilesUpgradeDialog = false
                        onOpenFiles()
                    },
                    style = HavnButtonStyle.Primary
                )
            },
            dismissButton = {
                TextButton(onClick = { showFilesUpgradeDialog = false }) {
                    Text(strings.commonDismiss)
                }
            }
        )
    }

    val activeProperties = remember(propertiesState.properties) {
        propertiesState.properties.filter { it.status == PropertyStatus.Active }
    }
    val activePropertyIds = remember(activeProperties) {
        activeProperties.map { it.id }.toSet()
    }
    val featuredOpenHouse = remember(openHousesState.openHouses, activePropertyIds) {
        openHousesState.openHouses
            .filter { it.propertyId in activePropertyIds }
            .closestDashboardOpening()
    }
    val featuredProperty = featuredOpenHouse?.let { openHouse ->
        activeProperties.firstOrNull { it.id == openHouse.propertyId }
    } ?: activeProperties.firstOrNull()
        ?: propertiesState.properties.maxByOrNull { it.updatedAt ?: it.createdAt ?: "" }
    val hasResolvedPropertyContent = propertiesState.hasLoadedInitialPage || propertiesState.properties.isNotEmpty()
    val featuredPropertyOpenHouses = remember(featuredProperty?.id, openHousesState.openHouses) {
        featuredProperty?.id?.let { propertyId ->
            openHousesState.openHouses.filter { it.propertyId == propertyId }
        }.orEmpty()
    }
    val featuredPropertyOpenHouse = remember(featuredPropertyOpenHouses) {
        featuredPropertyOpenHouses.closestDashboardOpening()
    } ?: featuredOpenHouse?.takeIf { it.propertyId == featuredProperty?.id }
    LaunchedEffect(featuredProperty?.id) {
        featuredProperty?.id?.let(propertiesViewModel::warmPropertyPhotos)
    }
    LaunchedEffect(
        featuredProperty?.id,
        featuredProperty?.stats?.openingCount,
        featuredPropertyOpenHouses.size,
        openHousesState.isLoading
    ) {
        val property = featuredProperty ?: return@LaunchedEffect
        if (
            property.stats.openingCount > 0 &&
            featuredPropertyOpenHouses.isEmpty() &&
            !openHousesState.isLoading
        ) {
            openHousesViewModel.loadOpenHousesForProperty(property.id)
        }
    }
    val pendingFollowUps = followUpsState.followUps.count { it.status == FollowUpStatus.Pending }
    val overdueTasks = remember(tasksState.tasks) {
        tasksState.tasks
            .filter { it.isOverdueForToday() }
            .sortedBy { it.dueAt ?: "" }
            .take(5)
    }
    val recentCheckIns = leadsState.leads
        .take(3)
        .map { lead -> "${lead.fullName} · ${lead.sourceProperty.title}" }
    val nextOpeningLine = featuredPropertyOpenHouse?.let { opening ->
        "${opening.displayTitle} · ${opening.startsAt.dateLabel()} · ${opening.timeRange()}"
    } ?: strings.noOpenHouseScheduled
    val nextAction = when {
        featuredPropertyOpenHouse != null -> strings.openKioskForNextOpening
        featuredProperty != null -> strings.createOpeningToStart
        else -> strings.addFirstPropertyToStart
    }
    val refreshing = propertiesState.isLoading ||
        openHousesState.isLoading ||
        leadsState.isLoading ||
        followUpsState.isLoading ||
        tasksState.isLoading ||
        agencyState.isLoading
    val scrollState = rememberScrollState()
    fun refreshToday() {
        propertiesViewModel.refresh()
        openHousesViewModel.refresh()
        leadsViewModel.refresh()
        followUpsViewModel.refresh()
        tasksViewModel.refresh()
        agencyViewModel.refresh()
    }
    var handledScrollToTopSignal by remember { mutableStateOf(scrollToTopSignal) }
    LaunchedEffect(scrollToTopSignal) {
        if (scrollToTopSignal > handledScrollToTopSignal) {
            if (scrollState.value == 0) {
                refreshToday()
            } else {
                scrollState.animateScrollTo(0)
            }
        }
        handledScrollToTopSignal = scrollToTopSignal
    }
    val horizontalPadding = if (havnIsWebShell) 28.dp else 18.dp
    val verticalPadding = if (havnIsWebShell) 24.dp else 18.dp
    val bottomPadding = if (havnNeedsNativeTabBarContentInset) 132.dp else verticalPadding
    var gestureStartedAtTop by remember { mutableStateOf(false) }
    var showAgencyChooser by remember { mutableStateOf(false) }

    HavnPullToRefreshBox(
        isRefreshing = refreshing,
        onRefresh = {
            if (gestureStartedAtTop) {
                refreshToday()
            }
        },
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(scrollState) { gestureStartedAtTop = it }
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.background, theme.gradientStart, theme.background)))
                .verticalScroll(scrollState)
                .padding(start = horizontalPadding, top = verticalPadding, end = horizontalPadding, bottom = bottomPadding)
        ) {
            val wide = (havnIsWebShell && maxWidth >= 980.dp) ||
                (havnIsTabletDevice() && maxWidth >= 720.dp)
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(if (wide) 18.dp else 14.dp)
            ) {
                TodayHeader(
                    firstName = firstName,
                    workspaceName = agencyState.workspaceName,
                    agencyIconUrl = agencyState.selectedAgency?.iconUrl ?: agencyState.agency?.iconUrl,
                    showAgencyButton = showHeaderActions && agencyState.entitlement.isProAgent,
                    showSettingsButton = showHeaderActions,
                    showTasksButton = showHeaderActions && !wide && canUseTasks,
                    showReportsButton = showHeaderActions && !wide && canUseReports,
                    showAnalyticsButton = showHeaderActions && !wide && canUseAnalytics,
                    onOpenSettings = onOpenSettings,
                    onOpenTasks = onOpenTasks,
                    onOpenReports = onOpenReports,
                    onOpenAnalytics = onOpenAnalytics,
                    onOpenAgency = { showAgencyChooser = true }
                )

                if (showHeaderActions && wide && (canUseTasks || canUseReports || canUseAnalytics)) {
                    PlanWorkspaceActions(
                        showTasks = canUseTasks,
                        showReports = canUseReports,
                        showAnalytics = canUseAnalytics,
                        onOpenReports = onOpenReports,
                        onOpenAnalytics = onOpenAnalytics,
                        onOpenTasks = onOpenTasks
                    )
                }

                AgencyInvitationsSection(
                    invitations = agencyState.pendingInvitations,
                    respondingInvitationId = agencyState.respondingInvitationId,
                    onAccept = agencyViewModel::acceptInvitation,
                    onDecline = agencyViewModel::declineInvitation
                )

                if (wide) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                        verticalAlignment = Alignment.Top
                    ) {
                        Column(modifier = Modifier.weight(1.55f), verticalArrangement = Arrangement.spacedBy(18.dp)) {
                            TodayPropertyArea(
                                featuredProperty = featuredProperty,
                                hasResolvedPropertyContent = hasResolvedPropertyContent,
                                featuredOpenHouse = featuredPropertyOpenHouse,
                                openings = featuredPropertyOpenHouses,
                                onAddProperty = onAddProperty,
                                onScheduleOpenHouse = { propertyId -> onScheduleOpenHouse(propertyId) },
                                onStartGuestCheckIn = {
                                    featuredPropertyOpenHouse?.let { openHousesViewModel.showLiveDashboard(it.id) }
                                        ?: featuredPropertyOpenHouses.firstOrNull()?.let { onOpenOpening(it.id) }
                                        ?: onOpenProperties()
                                },
                                onSendFollowUp = onOpenLeads,
                                onOpenOpening = onOpenOpening,
                                onViewProperty = onViewProperty
                            )
                        }
                        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(18.dp)) {
                            OverdueTasksCard(
                                tasks = overdueTasks,
                                visible = canUseTasks,
                                onOpenTasks = onOpenTasks,
                                onOpenTask = onOpenTask
                            )
                            TodayActionPanel(
                                nextOpening = nextOpeningLine,
                                recentCheckIns = recentCheckIns,
                                pendingFollowUps = pendingFollowUps,
                                nextAction = nextAction
                            )
                            QuickActions(
                                onNewProperty = onAddProperty,
                                onAddContact = onAddContact,
                                onOpenFiles = openFilesAction
                            )
                            TodayOverview(
                                visitors = openHousesState.visitorCount,
                                hotLeads = leadsState.leads.count { it.temperature == LeadTemperature.Hot },
                                followUps = pendingFollowUps
                            )
                            AppVersionText()
                        }
                    }
                } else {
                    TodayPropertyArea(
                        featuredProperty = featuredProperty,
                        hasResolvedPropertyContent = hasResolvedPropertyContent,
                        featuredOpenHouse = featuredPropertyOpenHouse,
                        openings = featuredPropertyOpenHouses,
                        onAddProperty = onAddProperty,
                        onScheduleOpenHouse = { propertyId -> onScheduleOpenHouse(propertyId) },
                        onStartGuestCheckIn = {
                            featuredPropertyOpenHouse?.let { openHousesViewModel.showLiveDashboard(it.id) }
                                ?: featuredPropertyOpenHouses.firstOrNull()?.let { onOpenOpening(it.id) }
                                ?: onOpenProperties()
                        },
                        onSendFollowUp = onOpenLeads,
                        onOpenOpening = onOpenOpening,
                        onViewProperty = onViewProperty
                    )

                    OverdueTasksCard(
                        tasks = overdueTasks,
                        visible = canUseTasks,
                        onOpenTasks = onOpenTasks,
                        onOpenTask = onOpenTask
                    )

                    TodayActionPanel(
                        nextOpening = nextOpeningLine,
                        recentCheckIns = recentCheckIns,
                        pendingFollowUps = pendingFollowUps,
                        nextAction = nextAction
                    )

                    QuickActions(
                        onNewProperty = onAddProperty,
                        onAddContact = onAddContact,
                        onOpenFiles = openFilesAction
                    )

                    TodayOverview(
                        visitors = openHousesState.visitorCount,
                        hotLeads = leadsState.leads.count { it.temperature == LeadTemperature.Hot },
                        followUps = pendingFollowUps
                    )
                    AppVersionText()
                }
            }
        }
    }

    if (showAgencyChooser) {
        AgencyChooserDialog(
            state = agencyState,
            onSelectAgency = agencyViewModel::selectAgency,
            onDismiss = { showAgencyChooser = false },
            onGoToAgency = {
                showAgencyChooser = false
                onOpenAgency()
            }
        )
    }
}

@Composable
private fun AgencyInvitationsSection(
    invitations: List<AgencyInvitation>,
    respondingInvitationId: String?,
    onAccept: (String) -> Unit,
    onDecline: (String) -> Unit
) {
    if (invitations.isEmpty()) return

    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        invitations.forEach { invitation ->
            AgencyInvitationCard(
                invitation = invitation,
                isResponding = respondingInvitationId == invitation.id,
                onAccept = { onAccept(invitation.id) },
                onDecline = { onDecline(invitation.id) }
            )
        }
    }
}

@Composable
private fun AgencyInvitationCard(
    invitation: AgencyInvitation,
    isResponding: Boolean,
    onAccept: () -> Unit,
    onDecline: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.55f))) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Surface(
                modifier = Modifier.size(42.dp),
                shape = CircleShape,
                color = theme.primaryAccent.copy(alpha = 0.15f),
                border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.35f))
            ) {
                Box(contentAlignment = Alignment.Center) {
                    AgencyLogoImage(
                        iconUrl = invitation.agencyIconUrl,
                        fallbackIcon = HavnIconKey.Users,
                        modifier = Modifier.size(42.dp)
                    )
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    text = strings.agencyInvitation,
                    color = theme.secondaryAccent,
                    style = MaterialTheme.typography.labelLarge
                )
                Text(
                    text = "${strings.joinAgency} ${invitation.agencyName}",
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleMedium
                )
                Text(
                    text = "${strings.youWereInvitedAs} ${invitation.role.displayName.lowercase()}${invitation.invitedByName?.let { " ${strings.invitedBy} $it" } ?: ""}.",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    HavnButton(
                        text = strings.accept,
                        onClick = onAccept,
                        modifier = Modifier.weight(1f),
                        loading = isResponding,
                        enabled = !isResponding,
                        leadingIcon = HavnIconKey.Check
                    )
                    HavnButton(
                        text = strings.decline,
                        onClick = onDecline,
                        modifier = Modifier.weight(1f),
                        style = HavnButtonStyle.Secondary,
                        enabled = !isResponding
                    )
                }
            }
        }
    }
}

@Composable
private fun TodayPropertyArea(
    featuredProperty: Property?,
    hasResolvedPropertyContent: Boolean,
    featuredOpenHouse: OpenHouse?,
    openings: List<OpenHouse>,
    onAddProperty: () -> Unit,
    onScheduleOpenHouse: (String) -> Unit,
    onStartGuestCheckIn: () -> Unit,
    onSendFollowUp: () -> Unit,
    onOpenOpening: (String) -> Unit,
    onViewProperty: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        if (!hasResolvedPropertyContent && featuredProperty == null) {
            TodayPropertyLoadingCard()
        } else if (featuredProperty == null) {
            EmptyState(
                title = strings.noActivePropertiesTitle,
                body = strings.noActivePropertiesBody
            )
            FirstRunGuide(
                onAddProperty = onAddProperty,
                onScheduleOpenHouse = { featuredProperty?.let { onScheduleOpenHouse(it.id) } ?: onAddProperty() },
                onStartGuestCheckIn = onStartGuestCheckIn,
                onSendFollowUp = onSendFollowUp
            )
        } else {
            FeaturedOpenHouseCard(
                property = featuredProperty,
                openHouse = featuredOpenHouse,
                openings = openings,
                onOpenOpening = onOpenOpening,
                onViewProperty = { onViewProperty(featuredProperty.id) }
            )
        }
    }
}

@Composable
private fun TodayPropertyLoadingCard() {
    val theme = LocalHavnThemeTokens.current
    HavnCard(
        modifier = Modifier
            .fillMaxWidth()
            .height(220.dp),
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Surface(
                modifier = Modifier.size(42.dp),
                shape = CircleShape,
                color = theme.primaryAccent.copy(alpha = 0.12f),
                border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.24f))
            ) {
                Box(contentAlignment = Alignment.Center) {
                    HavnIcon(
                        icon = HavnIconKey.Home,
                        contentDescription = null,
                        tint = theme.primaryAccent,
                        modifier = Modifier.size(20.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun PlanWorkspaceActions(
    showTasks: Boolean,
    showReports: Boolean,
    showAnalytics: Boolean,
    onOpenReports: () -> Unit,
    onOpenAnalytics: () -> Unit,
    onOpenTasks: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        if (showTasks) {
            QuickActionTile(
                icon = HavnIconKey.Check,
                title = strings.tasksTitle,
                body = strings.quickActionTasksBody,
                accent = theme.success,
                modifier = Modifier.weight(1f),
                onClick = onOpenTasks
            )
        }
        if (showReports) {
            QuickActionTile(
                icon = HavnIconKey.Reports,
                title = strings.sellerReportsTitle,
                body = strings.quickActionReportsBody,
                accent = theme.primaryAccent,
                modifier = Modifier.weight(1f),
                onClick = onOpenReports
            )
        }
        if (showAnalytics) {
            QuickActionTile(
                icon = HavnIconKey.Chart,
                title = strings.navAnalytics,
                body = strings.quickActionAnalyticsBody,
                accent = theme.cold,
                modifier = Modifier.weight(1f),
                onClick = onOpenAnalytics
            )
        }
    }
}

@Composable
private fun AgencyChooserDialog(
    state: AgencyUiState,
    onSelectAgency: (String) -> Unit,
    onDismiss: () -> Unit,
    onGoToAgency: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = theme.surface,
        title = {
            Text(strings.commonAgency, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                state.agencies.forEach { agency ->
                    AgencyChooserRow(
                        agency = agency,
                        selected = agency.id == state.selectedAgencyId,
                        onClick = { onSelectAgency(agency.id) }
                    )
                }
                if (state.agencies.isEmpty()) {
                    AgencyChooserRow(
                        title = AgencyUiState.SOLO_AGENCY_NAME,
                        subtitle = strings.yourDefaultAgency,
                        selected = true,
                        onClick = {}
                    )
                }
                Text(
                    strings.chooseAgencyBody,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        },
        confirmButton = {
            HavnButton(
                text = strings.commonAgency,
                onClick = onGoToAgency,
                leadingIcon = HavnIconKey.ArrowRight
            )
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.commonCancel, color = theme.primaryAccent)
            }
        }
    )
}

@Composable
private fun AgencyChooserRow(
    agency: Agency,
    selected: Boolean,
    onClick: () -> Unit
) {
    val strings = LocalHavnStrings.current
    AgencyChooserRow(
        title = agency.name,
        subtitle = "${agency.role.displayName} - ${agency.memberCount} ${strings.agencyMembersSuffix}",
        iconUrl = agency.iconUrl,
        selected = selected,
        onClick = onClick
    )
}

@Composable
private fun AgencyChooserRow(
    title: String,
    subtitle: String,
    iconUrl: String? = null,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.16f) else theme.card,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            AgencyLogoImage(
                iconUrl = iconUrl,
                fallbackIcon = HavnIconKey.Home,
                contentDescription = null,
                modifier = Modifier.size(28.dp)
            )
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
                Text(title, color = theme.primaryText, style = MaterialTheme.typography.titleSmall)
                Text(subtitle, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall)
            }
            if (selected) {
                HavnIcon(
                    icon = HavnIconKey.Check,
                    contentDescription = null,
                    tint = theme.primaryAccent,
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    }
}

@Composable
private fun FirstRunGuide(
    onAddProperty: () -> Unit,
    onScheduleOpenHouse: () -> Unit,
    onStartGuestCheckIn: () -> Unit,
    onSendFollowUp: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.firstRunStepsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            FirstRunStep("1", strings.firstRunAddProperty, onClick = onAddProperty)
            FirstRunStep("2", strings.firstRunScheduleOpening, onClick = onScheduleOpenHouse)
            FirstRunStep("3", strings.firstRunStartGuestCheckIn, onClick = onStartGuestCheckIn)
            FirstRunStep("4", strings.firstRunSendFollowUp, onClick = onSendFollowUp)
        }
    }
}

@Composable
private fun FirstRunStep(number: String, label: String, onClick: () -> Unit) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.medium)
            .clickable(onClick = onClick)
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Surface(
            modifier = Modifier.size(28.dp),
            shape = CircleShape,
            color = theme.primaryAccent.copy(alpha = 0.18f),
            border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.55f))
        ) {
            Box(contentAlignment = Alignment.Center) {
                Text(number, color = theme.secondaryAccent, style = MaterialTheme.typography.labelLarge)
            }
        }
        Text(label, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
        HavnIcon(
            icon = HavnIconKey.ArrowRight,
            contentDescription = null,
            tint = theme.primaryAccent,
            modifier = Modifier.size(16.dp)
        )
    }
}

@Composable
private fun TodayHeader(
    firstName: String?,
    workspaceName: String,
    agencyIconUrl: String?,
    showAgencyButton: Boolean,
    showSettingsButton: Boolean,
    showTasksButton: Boolean,
    showReportsButton: Boolean,
    showAnalyticsButton: Boolean,
    onOpenSettings: () -> Unit,
    onOpenTasks: () -> Unit,
    onOpenReports: () -> Unit,
    onOpenAnalytics: () -> Unit,
    onOpenAgency: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val displayedWorkspaceName = when (workspaceName) {
        "Personal Workspace" -> strings.personalAccount
        else -> workspaceName
    }
    val displayedAgentName = firstName?.takeIf { it.isNotBlank() }
    val identityLine = listOfNotNull(
        displayedAgentName,
        "@$displayedWorkspaceName"
    ).joinToString(" ")
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (showAgencyButton) {
                    TodayIconButton(
                        icon = HavnIconKey.Home,
                        imageUrl = agencyIconUrl,
                        contentDescription = strings.commonAgency,
                        onClick = onOpenAgency
                    )
                }
                Text(
                    text = strings.greetingNamePrefix,
                    color = theme.primaryText,
                    style = HavnTopHeaderTextStyle,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            if (showSettingsButton || showTasksButton || showReportsButton || showAnalyticsButton) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (showTasksButton) {
                        TodayIconButton(
                            icon = HavnIconKey.Check,
                            accent = theme.success,
                            contentDescription = strings.tasksTitle,
                            onClick = onOpenTasks
                        )
                    }
                    if (showReportsButton) {
                        TodayIconButton(
                            icon = HavnIconKey.Reports,
                            accent = Color(0xFFB16CFF),
                            contentDescription = strings.sellerReportsTitle,
                            onClick = onOpenReports
                        )
                    }
                    if (showAnalyticsButton) {
                        TodayIconButton(
                            icon = HavnIconKey.Chart,
                            accent = theme.cold,
                            contentDescription = strings.navAnalytics,
                            onClick = onOpenAnalytics
                        )
                    }
                    if (showSettingsButton) {
                        TodayIconButton(
                            icon = HavnIconKey.Sliders,
                            contentDescription = strings.commonSettings,
                            onClick = onOpenSettings
                        )
                    }
                }
            }
        }
        Text(
            text = identityLine,
            color = theme.secondaryText,
            style = MaterialTheme.typography.bodyMedium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun TodayIconButton(
    icon: HavnIconKey,
    imageUrl: String? = null,
    accent: Color? = null,
    contentDescription: String,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val tint = accent ?: theme.primaryAccent
    Surface(
        modifier = Modifier
            .size(42.dp)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = tint.copy(alpha = 0.13f),
        border = BorderStroke(1.dp, tint.copy(alpha = 0.42f))
    ) {
        Box(contentAlignment = Alignment.Center) {
            AgencyLogoImage(
                iconUrl = imageUrl,
                fallbackIcon = icon,
                fallbackTint = tint,
                contentDescription = contentDescription,
                modifier = Modifier.size(42.dp)
            )
        }
    }
}

@Composable
private fun AgencyLogoImage(
    iconUrl: String?,
    fallbackIcon: HavnIconKey,
    modifier: Modifier = Modifier,
    fallbackTint: Color? = null,
    contentDescription: String? = null
) {
    val theme = LocalHavnThemeTokens.current
    val rendered = HavnDatabaseImage(
        dataUrl = iconUrl,
        previewDataUrl = havnAgencyMediaPreviewSource(iconUrl, 96),
        contentDescription = contentDescription,
        modifier = modifier.clip(MaterialTheme.shapes.large),
        contentScale = ContentScale.Crop
    )
    if (!rendered) {
        HavnIcon(
            icon = fallbackIcon,
            contentDescription = contentDescription,
            tint = fallbackTint ?: theme.primaryAccent,
            modifier = Modifier.size(20.dp)
        )
    }
}

@Composable
private fun FeaturedOpenHouseCard(
    property: Property,
    openHouse: OpenHouse?,
    openings: List<OpenHouse>,
    onOpenOpening: (String) -> Unit,
    onViewProperty: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(
        border = null,
        imageHeader = {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .clickable(onClick = onViewProperty)
            ) {
                DashboardPropertyImage(property = property)
                HavnStatusBadge(
                    status = property.status.toHavnStatus(),
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(10.dp)
                )
                Column(
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .fillMaxWidth()
                        .background(
                            Brush.verticalGradient(
                                listOf(
                                    Color.Transparent,
                                    Color.Black.copy(alpha = 0.34f),
                                    Color.Black.copy(alpha = 0.78f)
                                )
                            )
                        )
                        .padding(14.dp)
                ) {
                    Text(
                        property.title,
                        color = Color.White,
                        style = MaterialTheme.typography.titleLarge,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        property.displayAddress,
                        color = Color.White.copy(alpha = 0.94f),
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        property.displayCity,
                        color = Color.White.copy(alpha = 0.82f),
                        style = MaterialTheme.typography.bodyMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            TodayOpeningsList(
                openings = openings,
                featuredOpenHouse = openHouse,
                onOpenOpening = onOpenOpening
            )
        }
    }
}

@Composable
private fun TodayOpeningsList(
    openings: List<OpenHouse>,
    featuredOpenHouse: OpenHouse?,
    onOpenOpening: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val orderedOpenings = openings
        .sortedWith(compareBy<OpenHouse> {
            when (it.status) {
                OpenHouseStatus.Live -> 0
                OpenHouseStatus.Scheduled -> 1
                OpenHouseStatus.Completed -> 2
                OpenHouseStatus.Cancelled -> 3
            }
        }.thenBy { it.startsAt })

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(strings.nextOpening, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
        if (orderedOpenings.isEmpty()) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = theme.background.copy(alpha = 0.35f),
                shape = MaterialTheme.shapes.medium,
                border = BorderStroke(1.dp, theme.border)
            ) {
                Text(
                    text = strings.noOpenHousesBody,
                    modifier = Modifier.padding(14.dp),
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
            }
        } else {
            orderedOpenings.forEach { opening ->
                TodayOpeningRow(
                    opening = opening,
                    highlighted = opening.id == featuredOpenHouse?.id,
                    onClick = { onOpenOpening(opening.id) }
                )
            }
        }
    }
}

@Composable
private fun TodayActionPanel(
    nextOpening: String,
    recentCheckIns: List<String>,
    pendingFollowUps: Int,
    nextAction: String
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.todayActionsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            TodayPriorityRow(
                icon = HavnIconKey.Calendar,
                label = strings.whatOpeningNext,
                value = nextOpening
            )
            TodayPriorityRow(
                icon = HavnIconKey.Users,
                label = strings.whoCheckedIn,
                value = recentCheckIns.firstOrNull() ?: strings.noCheckInsYet,
                extra = recentCheckIns.drop(1)
            )
            TodayPriorityRow(
                icon = HavnIconKey.Message,
                label = strings.whoNeedsFollowUp,
                value = if (pendingFollowUps == 0) strings.noPendingFollowUps else "$pendingFollowUps ${strings.commonFollowUps.lowercase()}"
            )
            TodayPriorityRow(
                icon = HavnIconKey.Check,
                label = strings.whatShouldIDoNow,
                value = nextAction
            )
        }
    }
}

@Composable
private fun OverdueTasksCard(
    tasks: List<HavnTask>,
    visible: Boolean,
    onOpenTasks: () -> Unit,
    onOpenTask: (String) -> Unit
) {
    if (!visible || tasks.isEmpty()) return
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onOpenTasks),
        border = BorderStroke(1.dp, theme.danger.copy(alpha = 0.42f))
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    modifier = Modifier.size(34.dp),
                    shape = CircleShape,
                    color = theme.danger.copy(alpha = 0.14f),
                    border = BorderStroke(1.dp, theme.danger.copy(alpha = 0.32f))
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        HavnIcon(
                            icon = HavnIconKey.Alert,
                            contentDescription = null,
                            tint = theme.danger,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                }
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Text(
                        text = "${strings.taskStatusOverdue} ${strings.tasksTitle.lowercase()}",
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        text = "${tasks.size} ${strings.tasksTitle.lowercase()}",
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodySmall,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                HavnIcon(
                    icon = HavnIconKey.ArrowRight,
                    contentDescription = strings.tasksTitle,
                    tint = theme.danger,
                    modifier = Modifier.size(19.dp)
                )
            }
            tasks.forEach { task ->
                OverdueTaskRow(
                    task = task,
                    onClick = { onOpenTask(task.id) }
                )
            }
        }
    }
}

@Composable
private fun OverdueTaskRow(
    task: HavnTask,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.Top
    ) {
        Surface(
            modifier = Modifier.size(8.dp).padding(top = 6.dp),
            shape = CircleShape,
            color = theme.danger
        ) {}
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(
                text = task.title,
                color = theme.primaryText,
                style = MaterialTheme.typography.bodyMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = task.dueAt?.readableDashboardDateTime() ?: task.status.label,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodySmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun TodayPriorityRow(
    icon: HavnIconKey,
    label: String,
    value: String,
    extra: List<String> = emptyList()
) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.Top
    ) {
        Surface(
            modifier = Modifier.size(32.dp),
            shape = CircleShape,
            color = theme.primaryAccent.copy(alpha = 0.14f),
            border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.28f))
        ) {
            Box(contentAlignment = Alignment.Center) {
                HavnIcon(icon, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(17.dp))
            }
        }
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text(label, color = theme.secondaryAccent, style = MaterialTheme.typography.labelLarge)
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
            extra.forEach { line ->
                Text(line, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}

@Composable
private fun TodayOpeningRow(
    opening: OpenHouse,
    highlighted: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        color = if (highlighted) theme.surface else theme.background.copy(alpha = 0.35f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, if (highlighted) theme.primaryAccent.copy(alpha = 0.72f) else theme.border)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(3.dp)
            ) {
                Text(
                    text = opening.displayTitle,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = "${opening.startsAt.dateLabel()} · ${opening.timeRange()}",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(5.dp)
            ) {
                HavnStatusBadge(status = opening.status.toHavnStatus(PropertyStatus.Active))
                Text(
                    text = "${opening.stats.visitorCount} ${strings.leadsMetric}",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.labelSmall
                )
            }
            HavnIcon(HavnIconKey.ArrowRight, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(16.dp))
        }
    }
}

@Composable
private fun BoxScope.DashboardPropertyImage(
    property: Property,
    selectedPhoto: PropertyPhoto? = null
) {
    val heroPhoto = selectedPhoto
        ?: property.photos.firstOrNull { it.storagePath == property.heroPhotoPath }
        ?: property.photos.firstOrNull { it.isHero }
        ?: property.photos.firstOrNull()

    DashboardPropertyImageContent(
        property = property,
        photo = heroPhoto,
        modifier = Modifier.fillMaxSize()
    )
}

@Composable
private fun DashboardPropertyImageContent(
    property: Property,
    photo: PropertyPhoto?,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Box(modifier = modifier) {
        val fullSizeSource = havnLargePropertyPhotoSource(photo?.storagePath)
            ?.takeUnless { it == photo?.thumbnailDataUrl }
        val rendered = HavnDatabaseImage(
            dataUrl = fullSizeSource ?: photo?.thumbnailDataUrl,
            previewDataUrl = photo?.thumbnailDataUrl,
            contentDescription = property.title,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(theme.surface)
            )
        }
    }
}

@Composable
private fun QuickActions(
    onNewProperty: () -> Unit,
    onAddContact: () -> Unit,
    onOpenFiles: () -> Unit
) {
    val strings = LocalHavnStrings.current
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        QuickActionTile(
            icon = HavnIconKey.Home,
            title = strings.quickActionAddPropertyTitle,
            body = strings.quickActionAddPropertyBody,
            modifier = Modifier.weight(1f),
            onClick = onNewProperty
        )
        QuickActionTile(
            icon = HavnIconKey.ContactCard,
            title = strings.quickActionAddContactTitle,
            body = strings.quickActionAddContactBody,
            modifier = Modifier.weight(1f),
            onClick = onAddContact
        )
        QuickActionTile(
            icon = HavnIconKey.Note,
            title = strings.quickActionFilesTitle,
            body = strings.quickActionFilesBody,
            modifier = Modifier.weight(1f),
            onClick = onOpenFiles
        )
    }
}

@Composable
private fun QuickActionTile(
    icon: HavnIconKey,
    title: String,
    body: String,
    accent: Color? = null,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val actionAccent = accent ?: theme.secondaryAccent
    Surface(
        modifier = modifier.clickable(onClick = onClick),
        color = accent?.copy(alpha = 0.12f) ?: theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, accent?.copy(alpha = 0.46f) ?: theme.border)
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 14.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            HavnIcon(icon, contentDescription = title, tint = actionAccent, modifier = Modifier.size(23.dp))
            Spacer(Modifier.height(8.dp))
            Text(title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(body, color = accent ?: theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

@Composable
private fun TodayOverview(
    visitors: Int,
    hotLeads: Int,
    followUps: Int
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.todaysOverview, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                OverviewMetric(visitors.toString(), strings.commonVisitors, Modifier.weight(1f))
                OverviewMetric(hotLeads.toString(), strings.hotLeads, Modifier.weight(1f))
                OverviewMetric(followUps.toString(), strings.commonFollowUps, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun AppVersionText() {
    val theme = LocalHavnThemeTokens.current
    Text(
        text = "Version ${havnAppVersionName()}",
        color = theme.mutedText,
        style = MaterialTheme.typography.labelSmall,
        modifier = Modifier.fillMaxWidth(),
        maxLines = 1
    )
}

@Composable
private fun OverviewMetric(value: String, label: String, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier,
        color = theme.background.copy(alpha = 0.35f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Column(
            modifier = Modifier.padding(vertical = 12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
            Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

private fun PropertyStatus.toHavnStatus(): HavnStatus = when (this) {
    PropertyStatus.Active -> HavnStatus.Active
    PropertyStatus.Planned -> HavnStatus.Planned
    PropertyStatus.Archived -> HavnStatus.Archived
}

private fun OpenHouseStatus?.toHavnStatus(propertyStatus: PropertyStatus): HavnStatus = when (this) {
    OpenHouseStatus.Live -> HavnStatus.Active
    OpenHouseStatus.Scheduled -> HavnStatus.Planned
    OpenHouseStatus.Completed,
    OpenHouseStatus.Cancelled -> HavnStatus.Archived
    null -> propertyStatus.toHavnStatus()
}

@OptIn(ExperimentalTime::class)
private fun String.instantMillisOrNull(): Long? = runCatching {
    Instant.parse(this).toEpochMilliseconds()
}.getOrNull()

private fun OpenHouse.timeRange(): String =
    "${startsAt.timeLabel()} - ${endsAt.timeLabel()}"

private fun String.dateLabel(): String =
    readableDashboardDate() ?: substringBefore("T").takeIf { it.isNotBlank() } ?: "Today"

@OptIn(ExperimentalTime::class)
private fun List<OpenHouse>.closestDashboardOpening(): OpenHouse? {
    if (isEmpty()) return null
    val nowMs = Clock.System.now().toEpochMilliseconds()
    firstOrNull { it.status == OpenHouseStatus.Live }?.let { return it }
    val scheduled = filter { it.status == OpenHouseStatus.Scheduled }
    scheduled
        .mapNotNull { opening -> opening.startsAt.instantMillisOrNull()?.let { it to opening } }
        .filter { (startsAtMs, _) -> startsAtMs >= nowMs }
        .minByOrNull { (startsAtMs, _) -> startsAtMs }
        ?.second
        ?.let { return it }
    scheduled
        .mapNotNull { opening -> opening.startsAt.instantMillisOrNull()?.let { it to opening } }
        .maxByOrNull { (startsAtMs, _) -> startsAtMs }
        ?.second
        ?.let { return it }
    return scheduled.firstOrNull() ?: firstOrNull()
}

@OptIn(ExperimentalTime::class)
private fun HavnTask.isOverdueForToday(): Boolean {
    if (status == HavnTaskStatus.Done || status == HavnTaskStatus.Cancelled) return false
    val due = dueAt ?: return false
    val dueMs = due.instantMillisOrNull()
    return if (dueMs != null) {
        dueMs < Clock.System.now().toEpochMilliseconds()
    } else {
        due < Clock.System.now().toString()
    }
}

private fun String.readableDashboardDateTime(): String {
    val clean = replace("T", " ").substringBefore(".").substringBefore("+").substringBefore("Z")
    val date = clean.substringBefore(" ").readableDashboardDate()
    val time = clean.substringAfter(" ", "").timeLabel()
    return listOfNotNull(date, time.takeIf { it.isNotBlank() }).joinToString(" · ").ifBlank { clean }
}

private fun String.readableDashboardDate(): String? {
    val date = substringBefore("T").substringBefore(" ").takeIf { it.isNotBlank() } ?: return null
    val parts = date.split("-")
    if (parts.size != 3) return null
    val month = parts[1].toIntOrNull()?.let { dashboardMonthNames.getOrNull(it - 1) } ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return "$month $day"
}

private fun String.timeLabel(): String {
    val raw = substringAfter("T", this).substringBefore(".").substringBefore("+").substringBefore("Z")
    val parts = raw.split(":")
    val hour24 = parts.getOrNull(0)?.toIntOrNull() ?: return raw
    val minute = parts.getOrNull(1)?.toIntOrNull() ?: 0
    val suffix = if (hour24 >= 12) "PM" else "AM"
    val hour12 = when (val normalized = hour24 % 12) {
        0 -> 12
        else -> normalized
    }
    return "$hour12:${minute.toString().padStart(2, '0')} $suffix"
}

private val dashboardMonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
